import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 
import {
    Plus, Search, Wrench, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, ArrowUpDown, Filter, RefreshCw, Box, X, Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme } from '../lib/swalTheme';
import { usePermissions } from './usePermissions';
import { useFavoriteFilters } from './useFavoriteFilters';
import { useFontSize } from './FontSizeContext';

const CraftList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // 🎯 إعدادات الترجمة والاتجاه
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { canCreate } = usePermissions('craft'); // 🎯 تأكد إن اسم الموديول صح عندك في الصلاحيات
    const [showAllSites, setShowAllSites] = useState(false);
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 12;

    // --- State الأساسي ---
    const [crafts, setCrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';
    
    // --- Params State ---
    const searchParams = new URLSearchParams(location.search);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [limit] = useState(50);
    const [sort, setSort] = useState({ 
        column: searchParams.get('sortBy') || 'craft_code', 
        direction: searchParams.get('sortDir') || 'asc' 
    });

    // --- 🎯 فلاتر الأعمدة ---
    const [filters, setFilters] = useState({
        craft_code: searchParams.get('craft_code') || "",
        description: searchParams.get('description') || "",
        siteid: searchParams.get('siteid') || ""
    });

    const { saveFavorite } = useFavoriteFilters('CRAFTS');
    const handleSaveFavoriteFilter = async () => {
        const { value: name } = await Swal.fire({
            ...swalTheme(),
            title: t('favorites.namePrompt', 'Name this favorite filter'),
            input: 'text',
            inputPlaceholder: t('favorites.namePlaceholder', 'e.g. My Open WOs'),
            showCancelButton: true,
            confirmButtonText: t('common.save', 'Save'),
            cancelButtonText: t('common.cancel', 'Cancel'),
            inputValidator: (value) => !value?.trim() && t('favorites.nameRequired', 'Please enter a name')
        });
        if (name && name.trim()) {
            saveFavorite(name, { ...filters, all_sites: showAllSites });
        }
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/crafts', {
                params: {
                    page,
                    limit,
                    all_sites: Boolean(showAllSites), 
                    user_siteid: sessionSite,
                    orgid: sessionOrg,
                    filter_siteid: filters.siteid || "", 
                    sortBy: sort.column,
                    sortDir: sort.direction,
                    craft_code: filters.craft_code,
                    description: filters.description,
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setCrafts(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            toast.error(t('crafts.messages.loadFailed')); // 🎯
        } finally {
            setLoading(false);
        }
    }, [page, showAllSites, filters, sort, sessionSite, sessionOrg, limit, t]);

    useEffect(() => {
        const timer = setTimeout(() => { fetchData(); }, 400); 
        return () => clearTimeout(timer);
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1); 
    };

    const handleRowClick = (craftCode) => {
        const queryParams = new URLSearchParams({
            page,
            ...filters,
            sortBy: sort.column,
            sortDir: sort.direction,
            all_sites: showAllSites,
            orgid: sessionOrg
        }).toString();
        navigate(`/crafts/${craftCode}?${queryParams}`);
    };

    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const clearFilters = () => {
        setFilters({ craft_code: "", description: "", siteid: "" });
        setPage(1);
    };

    useEffect(() => {
        const openAllSites = () => { setShowAllSites(true); setPage(1); };
        const viewMySite = () => { setShowAllSites(false); setPage(1); };
        
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

    useEffect(() => {
        const applyFavorite = (e) => {
            if (e.detail?.appName === 'CRAFTS' && e.detail?.filters) {
                const { all_sites: favAllSites, ...favFilters } = e.detail.filters;
                setFilters(prev => ({ ...prev, ...favFilters }));
                if (favAllSites !== undefined) setShowAllSites(favAllSites);
                setPage(1);
            }
        };
        window.addEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
        return () => window.removeEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
    }, []);

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div 
            className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left font-black'}`}
            style={{ fontSize: `${fontSize}px` }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {/* Toolbar */}
            <div className="bg-surface-card p-3 border-b shadow-sm z-20">
                <div className={`flex justify-between items-center px-4`}>
                    <h2 className={`text-primary-dark font-black italic uppercase text-lg flex items-center gap-2`}>
                        <Wrench size={22} className="text-primary" />
                        {t('crafts.headers.craftList')} {/* 🎯 */}
                        <RefreshCw
                            size={18}
                            className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary/70 hover:text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`}
                            onClick={fetchData}
                        />
                    </h2>

                    <div className={`flex gap-3`}>
                        <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
                            <X size={16}/> {t('pr.actions.clearFilters')}
                        </button>
                        <button onClick={handleSaveFavoriteFilter} className="flex items-center gap-2 px-4 py-2 text-status-warning hover:opacity-70 font-bold transition-colors">
                            <Star size={14} /> {t('favorites.save', 'SAVE AS FAVORITE')}
                        </button>
                        {/* {canCreate && ( */}
                            <button onClick={() => navigate('/crafts/new')} className={`bg-primary-dark text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-black shadow-lg active:scale-95 transition-all`}>
                                <Plus size={16} /> {t('crafts.actions.newCraft')} {/* 🎯 */}
                            </button>
                        {/* )} */}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
                            {/* Header Labels */}
                            <tr>
                                <SortableHeader label={t('crafts.tables.craftCode')} sortKey="craft_code" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('crafts.tables.description')} sortKey="description" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('crafts.tables.stdRate')} sortKey="standard_rate" currentSort={sort} onSort={handleSort} align="center" isRTL={isRTL} />
                                <th className={`p-3 border-white/10 ${isRTL ? 'border-r text-center' : 'border-l text-center'}`}>{t('crafts.tables.site')}</th>
                                <th className={`p-3 border-white/10 ${isRTL ? 'border-r text-center' : 'border-l text-center'}`}>{t('crafts.tables.org')}</th>
                            </tr>
                            {/* Filter Row */}
                            <tr className="bg-surface-panel shadow-inner">
                                <td className={`p-1 border-line ${isRTL ? 'border-l' : 'border-r'}`}>
                                    <input className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.craft_code} onChange={e => handleFilterChange('craft_code', e.target.value)} />
                                </td>
                                <td className={`p-1 border-line ${isRTL ? 'border-l' : 'border-r'}`}>
                                    <input className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.description} onChange={e => handleFilterChange('description', e.target.value)} />
                                </td>
                                <td className={`p-1 border-line bg-surface-app/50 ${isRTL ? 'border-l' : 'border-r'}`}></td>
                                <td className={`p-1 border-line ${isRTL ? 'border-l' : 'border-r'}`}>
                                    <input className="w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card text-center" placeholder={t('common.filter')} value={filters.siteid} onChange={e => handleFilterChange('siteid', e.target.value)} />
                                </td>
                                <td className="p-1 border-line bg-surface-app/50"></td>
                            </tr>
                        </thead>
                        
                        <tbody className="divide-y font-bold">
                            {loading ? (
                                <tr><td colSpan="5" className="p-20 text-center text-ink-secondary font-black animate-pulse uppercase tracking-[0.2em]">{t('common.loading')}</td></tr>
                            ) : crafts.length > 0 ? (
                                crafts.map((c, i) => (
                                    <tr key={i} className={`hover:bg-primary-soft cursor-pointer transition-colors group ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleRowClick(c.craft_code)}>
                                        <td className={`p-3 font-black text-primary group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>{c.craft_code}</td>
                                        <td className={`p-3 text-ink font-bold italic ${isRTL ? 'border-l' : 'border-r'}`}>{c.description}</td>
                                        <td className={`p-3 font-mono text-status-success font-black italic bg-surface-panel/30 text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                                            {parseFloat(c.standard_rate || 0).toLocaleString()}
                                        </td>
                                        <td className={`p-3 text-center text-ink-secondary font-bold ${isRTL ? 'border-l' : 'border-r'}`}>{c.site_id || t('common.global')}</td>
                                        <td className="p-3 text-center text-ink-secondary font-black">{c.org_id}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('common.noData')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Footer */}
            <div className="bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black uppercase tracking-widest text-[10px]">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <Box size={14} className="text-primary"/>
                        <span>{t('common.showing')}: <span className="text-sky mx-1">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
                    </div>
                    <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r' : 'border-l'}`}>
                        <Filter size={14} className="text-status-warning"/>
                        <span>{t('common.total')}: <span className="text-status-warning mx-1">{total}</span></span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10">
                        <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <div className="px-4 text-[11px] font-mono border-x border-white/10 mx-1 flex items-center gap-2">
                            <span className="text-status-warning font-black text-sm">{page}</span>
                            <span className="text-white/50 text-[9px]">{t('common.of')} {totalPages}</span>
                        </div>
                        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Helpers ---
const SortableHeader = ({ label, sortKey, currentSort, onSort, align = "left", isRTL }) => {
    let justifyClass = 'justify-start';
    if (align === 'center') justifyClass = 'justify-center';
    if (align === 'right') justifyClass = isRTL ? 'justify-start' : 'justify-end';
    if (align === 'left' && isRTL) justifyClass = 'justify-end';

    return (
        <th className={`p-3 border-white/10 cursor-pointer hover:bg-black/20 transition-colors ${isRTL ? 'border-r text-right' : 'border-r text-left'}`} onClick={() => onSort(sortKey)}>
            <div className={`flex items-center gap-2 ${justifyClass}`}>
                {label}
                <ArrowUpDown size={12} className={currentSort.column === sortKey ? 'text-status-warning' : 'text-white/30'} />
            </div>
        </th>
    );
};

export default CraftList;